import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendUp, TrendDown, ArrowUpRight, ArrowDownRight } from '@phosphor-icons/react';
import { useCurrency } from '../context/CurrencyContext';
import LivePulseIndicator from './LivePulseIndicator';
import AnimatedCounter from './AnimatedCounter';
import PortfolioMiniChart from './PortfolioMiniChart';

/**
 * Hero portfolio card. Premium fintech look:
 *  - Large animated Total Portfolio Value with subtle trend arrow + % gain badge
 *  - Inline mini area chart (1D/1W/1M/1Y)
 *  - Compact secondary stats row
 *  - Premium live pulse indicator instead of "Updated at HH:MM"
 */
const RealTimeProfitTracker = ({ portfolio, lastUpdate, isUpdating }) => {
  const [valueChange, setValueChange] = useState(0);
  const [previousValue, setPreviousValue] = useState(null);
  const { format: fmtCcy } = useCurrency();

  useEffect(() => {
    if (portfolio?.summary?.total_portfolio_value !== undefined) {
      const v = portfolio.summary.total_portfolio_value;
      if (previousValue !== null && previousValue !== v) {
        setValueChange(v - previousValue);
        const t = setTimeout(() => setValueChange(0), 3000);
        return () => clearTimeout(t);
      }
      setPreviousValue(v);
    }
  }, [portfolio, previousValue]);

  if (!portfolio) return null;

  const summary = portfolio.summary;
  const totalValue = summary.total_portfolio_value || 0;
  const totalProfit = summary.total_profit_loss || 0;
  const initial = 25000;
  const totalReturn = ((totalValue - initial) / initial) * 100;
  const isProfit = totalProfit >= 0;
  const ReturnIcon = totalReturn >= 0 ? ArrowUpRight : ArrowDownRight;

  return (
    <motion.div
      whileHover={{ y: -2 }}
      transition={{ duration: 0.2 }}
      className="relative overflow-hidden glass-card p-6 sm:p-8"
      data-testid="portfolio-hero"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-32 -right-28 w-80 h-80 rounded-full bg-yellow-400/[0.04] blur-3xl" />

      {/* Top row: label + live indicator */}
      <div className="relative flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Total Portfolio Value</p>
          <div className="flex items-baseline gap-3 flex-wrap">
            <motion.h2
              key={Math.floor(totalValue / 1)}
              animate={valueChange !== 0 ? { scale: [1, 1.03, 1] } : { scale: 1 }}
              transition={{ duration: 0.5 }}
              className="font-unbounded font-bold text-white text-4xl sm:text-5xl tracking-tight"
              data-testid="portfolio-hero-value"
            >
              <AnimatedCounter value={totalValue} format={(n) => fmtCcy(n)} />
            </motion.h2>
            <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border ${
              totalReturn >= 0
                ? 'bg-emerald-500/10 text-emerald-300 border-emerald-400/30'
                : 'bg-pink-500/10 text-pink-300 border-pink-400/30'
            }`}>
              <ReturnIcon size={14} weight="bold" />
              <span className="font-mono text-sm font-bold">{totalReturn >= 0 ? '+' : ''}{totalReturn.toFixed(2)}%</span>
            </div>
            <AnimatePresence>
              {valueChange !== 0 && (
                <motion.span
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className={`inline-flex items-center gap-0.5 text-xs font-bold ${valueChange > 0 ? 'text-emerald-300' : 'text-pink-300'}`}
                >
                  {valueChange > 0 ? <TrendUp size={12} weight="fill" /> : <TrendDown size={12} weight="fill" />}
                  {fmtCcy(Math.abs(valueChange))}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>
        <LivePulseIndicator updating={isUpdating} lastUpdate={lastUpdate} />
      </div>

      {/* Mini chart */}
      <div className="relative mt-4 mb-5">
        <PortfolioMiniChart />
      </div>

      {/* Secondary stats */}
      <div className="relative grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Today&apos;s P/L</p>
          <p className={`font-mono font-bold text-base mt-0.5 ${isProfit ? 'text-emerald-300' : 'text-pink-300'}`}>
            {isProfit ? '+' : ''}<AnimatedCounter value={totalProfit} format={(n) => fmtCcy(n)} />
          </p>
        </div>
        <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Cash</p>
          <p className="font-mono font-bold text-white text-base mt-0.5">
            <AnimatedCounter value={summary.cash_balance || 0} format={(n) => fmtCcy(n)} />
          </p>
        </div>
        <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Invested</p>
          <p className="font-mono font-bold text-white text-base mt-0.5">
            <AnimatedCounter value={summary.total_value || 0} format={(n) => fmtCcy(n)} />
          </p>
        </div>
        <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Positions</p>
          <p className="font-mono font-bold text-white text-base mt-0.5">
            {portfolio.holdings?.length || 0}
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default RealTimeProfitTracker;
